import { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useInvitePreview, useRedeemInvite } from '../lib/api';
import { motion, AnimatePresence, springs, Pressable, FadeIn, PageTransition } from '../lib/motion';
import { haptic } from '../lib/haptics';

const CARD = 'bg-white rounded-2xl border border-slate-200/80 shadow-card';

/**
 * Invite codes are drawn from an unambiguous alphabet so they survive being
 * read aloud in a lecture hall (no O/0, no I/1/L).
 */
const CODE_ALPHABET = 'ABCDEFGHJKMNPQRSTUVWXYZ23456789';
const CODE_LENGTH = 6;

/**
 * Uppercase, then drop whitespace plus the hyphens, dashes and underscores
 * people type out of habit. Codes are alphanumeric, so anything else goes too.
 * Length is deliberately not capped in case longer codes ship later.
 */
function normalizeCode(raw: string): string {
  return raw.toUpperCase().replace(/[^A-Z0-9]/g, '');
}

/**
 * Turn whatever the invite endpoint threw into something a student can act on.
 * The API surfaces 404 not_found and 410 inactive / expired / exhausted.
 */
function friendlyInviteError(err: unknown): string {
  const message = err instanceof Error ? err.message : typeof err === 'string' ? err : '';
  const probe = message.toLowerCase();

  if (probe.includes('not_found') || probe.includes('not found') || probe.includes('no invite')) {
    return 'We could not find that code. Check it and try again.';
  }
  if (probe.includes('inactive') || probe.includes('disabled') || probe.includes('revoked')) {
    return 'That code has been turned off by the instructor.';
  }
  if (probe.includes('expired')) {
    return 'That code has expired.';
  }
  if (probe.includes('exhausted') || probe.includes('max_uses') || probe.includes('limit')) {
    return 'That code has reached its limit.';
  }

  // Prefer a human sentence from the server over our generic line, but never
  // show a bare error slug or the transport level fallback.
  const looksHuman = message.includes(' ') && !message.startsWith('Something went wrong');
  return looksHuman ? message : 'That code cannot be used.';
}

export function JoinClassPage() {
  const { code: codeParam } = useParams<{ code?: string }>();
  const navigate = useNavigate();

  const [input, setInput] = useState(() => (codeParam ? normalizeCode(codeParam) : ''));
  // The code we are actually asking the server about. Null while typing.
  const [submitted, setSubmitted] = useState<string | null>(() =>
    codeParam && normalizeCode(codeParam).length >= 4 ? normalizeCode(codeParam) : null,
  );
  const [joinError, setJoinError] = useState('');
  const [alreadyMember, setAlreadyMember] = useState(false);

  const redirectTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => () => {
    if (redirectTimer.current) clearTimeout(redirectTimer.current);
  }, []);

  // A code arriving in the URL should preview straight away.
  useEffect(() => {
    if (!codeParam) return;
    const next = normalizeCode(codeParam);
    setInput(next);
    setSubmitted(next.length >= 4 ? next : null);
  }, [codeParam]);

  const { data, isLoading, isFetching, error: previewError } = useInvitePreview(submitted);
  const redeem = useRedeemInvite();

  const invite = data?.invite;
  const previewing = Boolean(submitted) && (isLoading || isFetching) && !invite && !previewError;
  const canSubmit = normalizeCode(input).length >= 4;

  const handleChange = (raw: string) => {
    setInput(normalizeCode(raw));
    // Typing means they are correcting something, so drop back to entry mode.
    if (submitted) setSubmitted(null);
    if (joinError) setJoinError('');
  };

  const handlePreview = (e: React.FormEvent) => {
    e.preventDefault();
    const next = normalizeCode(input);
    if (next.length < 4) return;
    haptic.light();
    setJoinError('');
    setSubmitted(next);
  };

  const handleBack = () => {
    // Deep links land here with no history to pop, so fall back to home.
    if (window.history.length > 1) navigate(-1);
    else navigate('/');
  };

  const handleReset = () => {
    setSubmitted(null);
    setJoinError('');
  };

  const handleJoin = async () => {
    if (!submitted || !invite) return;
    setJoinError('');
    try {
      const result = await redeem.mutateAsync(submitted);
      const destination = `/class/${result.classSectionId || invite.classSectionId}`;
      if (result.alreadyMember) {
        haptic.light();
        setAlreadyMember(true);
        redirectTimer.current = setTimeout(() => {
          navigate(destination, { replace: true });
        }, 1200);
        return;
      }
      haptic.success();
      navigate(destination, { replace: true });
    } catch (err) {
      haptic.error();
      setJoinError(friendlyInviteError(err));
    }
  };

  return (
    <PageTransition className="max-w-lg mx-auto px-4 pb-24 space-y-5">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Pressable
          onClick={handleBack}
          className="flex h-11 w-11 -ml-2 items-center justify-center rounded-full text-slate-500 hover:text-squishy-dark transition-colors"
          aria-label="Back"
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
          </svg>
        </Pressable>
        <h1 className="text-xl font-bold font-display text-squishy-dark">Join a Class</h1>
      </div>

      {/* Code entry */}
      {!invite && (
        <FadeIn>
          <form onSubmit={handlePreview} className={`${CARD} p-6 space-y-5`}>
            <div className="text-center space-y-2">
              <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl bg-teal-50">
                <svg className="h-6 w-6 text-squishy-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15 7a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2m-4 0H5a2 2 0 01-2-2V7a2 2 0 012-2h2m0 12h2a2 2 0 012 2v2a2 2 0 01-2 2H7a2 2 0 01-2-2v-2a2 2 0 012-2z" />
                </svg>
              </div>
              <h2 className="text-base font-bold font-display text-squishy-dark">Enter your invite code</h2>
              <p className="text-sm text-slate-500 leading-relaxed">
                Your instructor gives out a {CODE_LENGTH} character code for your exact section.
              </p>
            </div>

            <div className="space-y-2">
              <label htmlFor="invite-code" className="sr-only">Invite code</label>
              <input
                id="invite-code"
                value={input}
                onChange={(e) => handleChange(e.target.value)}
                autoFocus={!codeParam}
                autoComplete="one-time-code"
                autoCapitalize="characters"
                autoCorrect="off"
                spellCheck={false}
                inputMode="text"
                enterKeyHint="go"
                placeholder="ABC123"
                aria-describedby="invite-code-help"
                className="w-full min-h-[64px] text-center font-mono text-3xl sm:text-4xl font-bold tracking-[0.3em] uppercase text-squishy-dark placeholder:text-slate-300 placeholder:tracking-[0.3em] bg-slate-50 border-2 border-slate-200 rounded-2xl px-3 py-4 focus:outline-none focus:border-squishy-primary focus:bg-white transition-colors"
              />
              <p id="invite-code-help" className="text-center text-xs text-slate-400">
                Letters and numbers only. We skip look alike characters (no O, no I).
              </p>
            </div>

            <AnimatePresence>
              {previewError && !previewing && (
                <motion.p
                  initial={{ opacity: 0, y: -4 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                  transition={springs.snappy}
                  role="alert"
                  className="text-sm text-red-500 text-center leading-relaxed"
                >
                  {friendlyInviteError(previewError)}
                </motion.p>
              )}
            </AnimatePresence>

            <Pressable
              type="submit"
              haptic="medium"
              disabled={!canSubmit || previewing}
              className="w-full min-h-[52px] bg-squishy-primary text-white font-semibold py-3.5 rounded-2xl shadow-soft disabled:opacity-50 transition-opacity"
            >
              {previewing ? (
                <span className="inline-flex items-center justify-center gap-2">
                  <span className="animate-spin w-4 h-4 border-2 border-white/30 border-t-white rounded-full" />
                  Checking code...
                </span>
              ) : (
                'Look up class'
              )}
            </Pressable>
          </form>
        </FadeIn>
      )}

      {/* Confirmation. Students land in the wrong section when they self enrol
          by search, so make them confirm the exact section first. */}
      {invite && (
        <FadeIn>
          <div className="space-y-5">
            <div className={`${CARD} p-6 space-y-5`}>
              <div className="text-center space-y-2">
                <span className="inline-flex items-center gap-1 text-xs font-semibold text-squishy-tealDark bg-teal-50 rounded-full px-2.5 py-1">
                  Code {invite.code}
                </span>
                <h2 className="text-base font-bold font-display text-squishy-dark">
                  You are about to join
                </h2>
              </div>

              <div className="rounded-2xl bg-slate-50 border border-slate-200/80 p-4 space-y-3">
                <div>
                  <div className="text-xs font-semibold uppercase tracking-wide text-slate-400">School</div>
                  <div className="text-sm font-semibold text-squishy-dark">{invite.schoolName}</div>
                </div>
                <div>
                  <div className="text-xs font-semibold uppercase tracking-wide text-slate-400">Class</div>
                  <div className="text-sm font-semibold text-squishy-dark">
                    {invite.classCode}
                    {invite.className ? `: ${invite.className}` : ''}
                  </div>
                </div>
                {invite.term && (
                  <div>
                    <div className="text-xs font-semibold uppercase tracking-wide text-slate-400">Term</div>
                    <div className="text-sm font-semibold text-squishy-dark">{invite.term}</div>
                  </div>
                )}
                {invite.professorName && (
                  <div>
                    <div className="text-xs font-semibold uppercase tracking-wide text-slate-400">Instructor</div>
                    <div className="text-sm font-semibold text-squishy-dark">{invite.professorName}</div>
                  </div>
                )}
              </div>

              <p className="text-xs text-slate-500 text-center leading-relaxed">
                Check that this is your section before you join. If it is not, go back and retype the code.
              </p>
            </div>

            <AnimatePresence>
              {alreadyMember && (
                <motion.div
                  initial={{ opacity: 0, y: -4 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                  transition={springs.snappy}
                  role="status"
                  className="flex items-center justify-center gap-2 text-sm font-semibold text-squishy-tealDark bg-teal-50 rounded-2xl px-4 py-3"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                  You are already in this class. Taking you there now.
                </motion.div>
              )}
              {joinError && (
                <motion.p
                  initial={{ opacity: 0, y: -4 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                  transition={springs.snappy}
                  role="alert"
                  className="text-sm text-red-500 text-center leading-relaxed"
                >
                  {joinError}
                </motion.p>
              )}
            </AnimatePresence>

            <div className="space-y-3">
              <Pressable
                haptic="medium"
                onClick={handleJoin}
                disabled={redeem.isPending || alreadyMember}
                className="w-full min-h-[52px] bg-squishy-primary text-white font-semibold py-3.5 rounded-2xl shadow-soft disabled:opacity-50 transition-opacity"
              >
                {redeem.isPending ? (
                  <span className="inline-flex items-center justify-center gap-2">
                    <span className="animate-spin w-4 h-4 border-2 border-white/30 border-t-white rounded-full" />
                    Joining...
                  </span>
                ) : (
                  'Join this class'
                )}
              </Pressable>

              <Pressable
                onClick={handleReset}
                disabled={redeem.isPending || alreadyMember}
                className="w-full min-h-[44px] text-sm font-semibold text-slate-500 py-2.5 rounded-2xl hover:text-squishy-dark disabled:opacity-50 transition-colors"
              >
                Use a different code
              </Pressable>
            </div>
          </div>
        </FadeIn>
      )}

      {!invite && (
        <p className="text-center text-xs text-slate-400 leading-relaxed">
          Codes use only these characters: {CODE_ALPHABET.split('').join(' ')}
        </p>
      )}
    </PageTransition>
  );
}

export default JoinClassPage;
